import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import VerifiedBadge from '../components/VerifiedBadge';
import {
  ArrowLeft,
  ShieldCheck,
  SpinnerGap,
  Clock,
  CheckCircle,
  XCircle,
  IdentificationCard,
  Buildings,
  CalendarBlank,
  User as UserIcon,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  pending: 'bg-amber-100 border-amber-400 text-amber-900',
  approved: 'bg-emerald-100 border-emerald-400 text-emerald-900',
  rejected: 'bg-red-100 border-red-400 text-red-900',
  withdrawn: 'bg-neutral-100 border-neutral-400 text-neutral-700',
};

function DocStrip({ urls, label }) {
  if (!urls || urls.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500 mb-1">{label}</p>
      <div className="flex flex-wrap gap-2">
        {urls.map((u, i) => {
          const isPdf = u.toLowerCase().endsWith('.pdf');
          const href = u.startsWith('http') ? u : `${API_URL}${u}`;
          return (
            <a
              key={u}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="block w-28 h-28 border-2 border-black rounded-lg overflow-hidden bg-white hover:shadow-[3px_3px_0px_#000] transition-all"
              data-testid={`admin-${label.toLowerCase().replace(/\s/g,'-')}-${i}`}
            >
              {isPdf ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-700">
                  <span className="text-xs font-black">Open PDF</span>
                </div>
              ) : (
                <img src={href} alt="doc" className="w-full h-full object-cover" />
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminVerifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('userType') !== 'admin') navigate('/login');
  }, [navigate]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = filter ? { status: filter } : {};
      const r = await axios.get(`${API_URL}/api/admin/verifications`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setItems(r.data?.verifications || []);
      setPendingCount(r.data?.pending_count || 0);
    } catch (e) {
      toast({ title: 'Failed to load queue', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openSubmission = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setRejectOpen(false);
    setRejectReason('');
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/admin/verifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetail(r.data?.verification || null);
    } catch (e) {
      toast({ title: 'Failed to load detail', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/verifications/${selectedId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Verified Pro approved' });
      await fetchList();
      await openSubmission(selectedId);
    } catch (e) {
      toast({ title: 'Approve failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      return toast({ title: 'Reason required', variant: 'destructive' });
    }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/verifications/${selectedId}/reject`, {
        reason: rejectReason.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Submission rejected' });
      setRejectOpen(false);
      setRejectReason('');
      await fetchList();
      await openSubmission(selectedId);
    } catch (e) {
      toast({ title: 'Reject failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="admin-verifications-page">
      {/* Header */}
      <div className="bg-neutral-900 text-white py-6 border-b-4 border-black">
        <div className="max-w-6xl mx-auto px-4 flex items-center gap-3">
          <button onClick={() => navigate('/admin')} className="text-white/80 hover:text-white" data-testid="back-btn">
            <ArrowLeft size={26} weight="bold" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
              <ShieldCheck size={28} weight="fill" className="text-emerald-400" />
              Verifications Queue
            </h1>
            <p className="text-neutral-300 text-sm mt-1">
              Review contractor license + insurance submissions.
              {pendingCount > 0 && <span className="text-amber-300 font-bold ml-2">{pendingCount} pending</span>}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-5 space-y-3">
          <div className="flex gap-2 flex-wrap">
            {['pending','approved','rejected','withdrawn'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 h-9 text-xs font-bold rounded-lg border-2 capitalize ${filter === f ? 'bg-black text-white border-black' : 'bg-white text-neutral-700 border-neutral-300 hover:border-black'}`}
                data-testid={`filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-6 bg-white border-2 border-black rounded-xl flex items-center gap-3 text-neutral-600">
              <SpinnerGap size={22} className="animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 bg-white border-2 border-dashed border-neutral-300 rounded-xl text-center text-neutral-500" data-testid="empty-queue">
              <CheckCircle size={36} weight="light" className="mx-auto mb-2 text-emerald-500" />
              No submissions in this view.
            </div>
          ) : (
            items.map((v) => (
              <button
                key={v.id}
                onClick={() => openSubmission(v.id)}
                className={`w-full text-left bg-white border-2 rounded-xl p-4 shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] transition-all ${selectedId === v.id ? 'border-[#2B4C6F]' : 'border-black'}`}
                data-testid={`verification-row-${v.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-neutral-900 truncate">{v.contractor_name || v.contractor_email}</p>
                    <p className="text-xs text-neutral-500 truncate">{v.contractor_email}</p>
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded border-2 flex-shrink-0 ${STATUS_TONE[v.status] || ''}`}>
                    {v.status}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span className="flex items-center gap-1">
                    <IdentificationCard size={12} weight="bold" /> {v.license_state} · {v.license_number}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} weight="bold" /> {v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {!selectedId ? (
            <div className="bg-white border-2 border-dashed border-neutral-300 rounded-xl p-8 text-center text-neutral-500" data-testid="no-selection-state">
              <ShieldCheck size={48} weight="light" className="mx-auto mb-3 text-neutral-300" />
              <p className="font-bold">Select a submission to review</p>
            </div>
          ) : !detail ? (
            <div className="bg-white border-2 border-black rounded-xl p-6 flex items-center gap-3 text-neutral-600">
              <SpinnerGap size={22} className="animate-spin" /> Loading…
            </div>
          ) : (
            <div className="bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_#000] overflow-hidden" data-testid="verification-detail">
              <div className="px-5 py-4 border-b-2 border-black flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                  <UserIcon size={24} weight="fill" className="text-neutral-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-black text-base text-neutral-900 break-words">{detail.contractor_name}</p>
                    <p className="text-xs text-neutral-500">{detail.contractor_email}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border-2 flex-shrink-0 ${STATUS_TONE[detail.status]}`}>
                  {detail.status}
                </span>
              </div>

              <div className="p-5 space-y-5">
                <section>
                  <h3 className="text-sm font-black uppercase tracking-wider text-neutral-700 mb-3 flex items-center gap-2">
                    <IdentificationCard size={16} weight="fill" /> License
                  </h3>
                  <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">Number</p>
                      <p className="font-bold">{detail.license_number}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">State</p>
                      <p className="font-bold">{detail.license_state}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">Expiry</p>
                      <p className="font-bold">{detail.license_expiry ? new Date(detail.license_expiry).toLocaleDateString() : '—'}</p>
                    </div>
                  </div>
                  <DocStrip urls={detail.license_document_urls} label="License docs" />
                </section>

                <section>
                  <h3 className="text-sm font-black uppercase tracking-wider text-neutral-700 mb-3 flex items-center gap-2">
                    <Buildings size={16} weight="fill" /> Insurance
                  </h3>
                  <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">Provider</p>
                      <p className="font-bold">{detail.insurance_provider}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">Policy #</p>
                      <p className="font-bold">{detail.insurance_policy_number}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">Expiry</p>
                      <p className="font-bold">{detail.insurance_expiry ? new Date(detail.insurance_expiry).toLocaleDateString() : '—'}</p>
                    </div>
                  </div>
                  <DocStrip urls={detail.insurance_document_urls} label="Insurance docs" />
                </section>

                {detail.notes && (
                  <section>
                    <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500 mb-1">Contractor notes</p>
                    <p className="text-sm text-neutral-700 whitespace-pre-wrap">{detail.notes}</p>
                  </section>
                )}

                {detail.reviewer_note && (
                  <section className="bg-neutral-50 border-2 border-neutral-200 rounded-lg p-3">
                    <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
                      <CalendarBlank size={12} weight="bold" /> Admin note ({detail.reviewed_at ? new Date(detail.reviewed_at).toLocaleString() : ''})
                    </p>
                    <p className="text-sm">{detail.reviewer_note}</p>
                  </section>
                )}

                {detail.status === 'pending' && (
                  <div className="border-t-2 border-neutral-200 pt-4">
                    {!rejectOpen ? (
                      <div className="flex gap-3">
                        <button
                          onClick={() => setRejectOpen(true)}
                          disabled={busy}
                          className="flex-1 h-11 bg-white text-red-700 font-bold rounded-lg border-2 border-red-500 hover:bg-red-50 flex items-center justify-center gap-2 disabled:opacity-50"
                          data-testid="open-reject-btn"
                        >
                          <XCircle size={18} weight="bold" /> Reject
                        </button>
                        <button
                          onClick={approve}
                          disabled={busy}
                          className="flex-1 h-11 bg-emerald-500 text-black font-black rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                          data-testid="approve-btn"
                        >
                          {busy ? <SpinnerGap size={20} className="animate-spin" /> : <><CheckCircle size={18} weight="fill" /> Approve <VerifiedBadge size="sm" label={false} /></>}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="reject-panel">
                        <Label className="font-bold block">Reason (visible to contractor)</Label>
                        <Textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          rows={3}
                          placeholder="What specifically needs to be fixed before re-submitting?"
                          className="border-2 border-black rounded-lg"
                          data-testid="reject-reason-input"
                        />
                        <div className="flex gap-3">
                          <button
                            onClick={() => { setRejectOpen(false); setRejectReason(''); }}
                            className="flex-1 h-11 bg-neutral-100 text-neutral-700 font-bold rounded-lg border-2 border-neutral-300"
                          >Cancel</button>
                          <button
                            onClick={reject}
                            disabled={busy}
                            className="flex-1 h-11 bg-red-500 text-white font-black rounded-lg border-2 border-black disabled:opacity-50"
                            data-testid="confirm-reject-btn"
                          >
                            {busy ? <SpinnerGap size={20} className="animate-spin" /> : 'Confirm reject'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
